import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { SignaturesAdapter } from '../../adapters/signatures.adapter';
import {
  createSignature,
  createSignatureSuccess,
  deleteSignature,
  deleteSignatureFailure,
  deleteSignatureSuccess,
  loadSignatures,
  loadSignaturesFailure,
  loadSignaturesSuccess,
  updateSignature,
  updateSignatureFailure,
  updateSignatureSuccess,
} from './signatures.actions';

@Injectable()
export class SignatureEffects {
  private readonly actions$ = inject(Actions);
  private readonly signaturesAdapter = inject(SignaturesAdapter);
  private readonly toastsService = inject(ToastsService);
  
  loadSignatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSignatures),
      exhaustMap(() =>
        this.signaturesAdapter.getSignatures().pipe(
          map((signatures) => loadSignaturesSuccess({ signatures })),
          catchError((error) => {
            return of(loadSignaturesFailure({ error }));
          })
        )
      )
    )
  );

  createSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createSignature),
      mergeMap((props) =>
        this.signaturesAdapter.createSignature(props.signature).pipe(
          map((response) => createSignatureSuccess({ signature: { ...props.signature, id: response.id } })),
          catchError((error) => {
            return of(deleteSignatureFailure({ error }));
          })
        )
      )
    )
  );

  createSignatureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createSignatureSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.profile.signatures.toasts.signature-created');
        })
      ),
    { dispatch: false }
  );

  updateSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSignature),
      mergeMap((props) =>
        this.signaturesAdapter.editSignature(props.signature).pipe(
          map(() => updateSignatureSuccess({ signature: props.signature })),
          catchError((error) => {
            return of(updateSignatureFailure({ error }));
          })
        )
      )
    )
  );

  updateSignatureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateSignatureSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.profile.signatures.toasts.signature-edited');
        })
      ),
    { dispatch: false }
  );

  deleteSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSignature),
      mergeMap((props) =>
        this.signaturesAdapter.deleteSignature(props.signature.id).pipe(
          map(() => deleteSignatureSuccess({ id: props.signature.id })),
          catchError((error) => {
            return of(deleteSignatureFailure({ error }));
          })
        )
      )
    )
  );

  deleteSignatureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteSignatureSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.profile.signatures.toasts.signature-deleted');
        })
      ),
    { dispatch: false }
  );

  loadSignaturesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadSignaturesFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
